import { syncRefs } from '@vueuse/core';
import { computed, onBeforeUnmount, ref, type Ref, watch } from 'vue';

import { marketService } from '@exchange/libs/market/service/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

import type { BookTickData } from './book-ticker-model';
import bookTickerService from './book-ticker.service';

export interface BookTickProvider {
  getBookTickForMarketId: (marketId: string) => Ref<BookTickData> | undefined;
}

export default function useBookTicker(marketIds: Ref<string | string[]>) {
  const bookTicks = ref<Record<string, Ref<BookTickData>>>({});
  const unsubscribes = ref<Record<string, () => void>>({});
  const syncStopHandles = ref<Record<string, () => void>>({});
  const isLoading = ref(true);

  const safeUnsubscribe = (marketId: string) => {
    try {
      syncStopHandles.value[marketId]?.();
      delete syncStopHandles.value[marketId];

      unsubscribes.value[marketId]?.();
      delete unsubscribes.value[marketId];

      delete bookTicks.value[marketId];
    } catch (e) {
      logger.log(`BookTicker: unsubscribing failed for ${marketId}`, e);
    }
  };

  const handleSubscribe = async (marketId: string) => {
    if (unsubscribes.value[marketId]) return; // Already subscribed

    isLoading.value = true;

    unsubscribes.value[marketId] = await bookTickerService.subscribe(marketId, {
      success: (res: { bookTick: Ref<BookTickData> }) => {
        bookTicks.value[marketId] = ref<BookTickData>({} as BookTickData);
        syncStopHandles.value[marketId] = syncRefs(res.bookTick, bookTicks.value[marketId] as Ref<BookTickData>);
        isLoading.value = false;
      },
      fail: (error) => {
        logger.error(`BookTicker: failed to subscribe to ${marketId}, retrying...`, error);
        handleSubscribe(marketId);
      },
    });
  };

  const getBookTickForMarketId = (marketId: string) => bookTicks.value[marketId];

  const ids = computed(() => (Array.isArray(marketIds.value) ? marketIds.value : [marketIds.value]));

  watch(
    ids,
    async (newIds, oldIds) => {
      const idsToSubscribe = newIds.filter((id) => !oldIds || !oldIds.includes(id));
      const idsToUnsubscribe = oldIds?.filter((id) => !newIds.includes(id)) ?? [];

      await Promise.all(idsToUnsubscribe.map(safeUnsubscribe));
      await marketService.awaitMarkets();
      await Promise.all(idsToSubscribe.map(handleSubscribe));
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    Object.keys(unsubscribes.value).forEach(safeUnsubscribe);
  });

  return {
    bookTicks,
    getBookTickForMarketId,
    isLoading,
  };
}
